import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { useRouteParams } from '../../hooks/useRouteParams';
import {
  addBlock,
  clearBlock,
  deleteBlock,
  initBlockData,
  resetBlock,
  updateBlockData,
} from '../../redux/schema/slice';
import Icon from '../../styled/Icon/Icon';
import Tooltip from '../../styled/Tooltip/Tooltip';
import formSchema from '../../templates/form';
import { camelize } from '../../utils';
import CodeRenderer from '../CodeRenderer';
import {
  EditorFormWrapper,
  FieldBlockWrapper,
  EditorFormHeader,
  FloatingActionsWrapper,
} from './EditorForm.styles';
import FormSchemaInput from './FormSchemaInput';
import Divider from '../../styled/Divider';
import boxTemplates from '../../templates/box';
import FORM_FIELDS from '../../templates/form/fields';
import { trackMixEvent, TRACK_EVENTS } from '../../services/analytics';

interface EditorFormProps {}

const EditorForm: React.FC<EditorFormProps> = () => {
  const dispatch = useAppDispatch();
  const { type, template } = useRouteParams();
  const { hasBody, fields, instances } = formSchema[type];
  const formData = useAppSelector(({ schema }) => schema);
  const isMultiForm = instances.max > 1;

  const currentTemplate = boxTemplates[type].find(
    ({ name }) => template === name
  );

  const hiddenFields = currentTemplate?.config.hiddenFields || [];
  const hiddenFieldsArr = hiddenFields.map((field) => camelize(field));

  const showBodyField =
    hasBody && !hiddenFieldsArr.includes(camelize(FORM_FIELDS.PRODUCT_BODY));

  const handleUpdateField = (
    e: React.ChangeEvent<HTMLInputElement>,
    id: string
  ) => {
    const { name, value } = e.target;
    const payload = {
      type,
      id,
      key: name,
      value,
    };
    dispatch(updateBlockData(payload));
  };
  const handleDeleteFormBlock = (id: string) => {
    trackMixEvent(TRACK_EVENTS.DELETE_BLOCK);
    dispatch(deleteBlock({ id }));
  };
  const handleAddFormBlock = () => {
    trackMixEvent(TRACK_EVENTS.ADD_BLOCK);
    dispatch(addBlock());
  };
  const handleClearBlock = (id: string) => {
    trackMixEvent(TRACK_EVENTS.CLEAR_BLOCK);
    dispatch(clearBlock({ id }));
  };
  const handleResetBlock = (id: string) => {
    trackMixEvent(TRACK_EVENTS.RESET_BLOCK);
    dispatch(resetBlock({ id }));
  };

  useEffect(() => {
    dispatch(
      initBlockData({
        type,
        instances,
      })
    );
  }, [dispatch, instances, type]);

  // Todo: Return a loader.
  if (formData === undefined) return null;
  const showAddBtn = isMultiForm && formData.fieldData.length < instances.max;
  const showDeleteBtn = isMultiForm && formData.fieldData.length > 1;
  return (
    <EditorFormWrapper>
      <EditorFormHeader>
        <h4>Configure Your Template</h4>
        <CodeRenderer />
      </EditorFormHeader>
      <p />
      <form>
        {formData.fieldData.map((data, i) => (
          <div key={data.id}>
            <FieldBlockWrapper
              hasBody={hasBody}
              style={{ gridTemplateColumns: showBodyField ? '' : '1fr' }}
            >
              <div className="fields">
                {fields.map((label) =>
                  !hiddenFieldsArr.includes(label) ? (
                    <FormSchemaInput
                      label={isMultiForm ? `${label} #${i + 1}` : label}
                      key={camelize(label)}
                      id={camelize(label)}
                      onChange={(e) => handleUpdateField(e, data.id)}
                      value={data.fields[camelize(label)]}
                    />
                  ) : undefined
                )}
              </div>
              {showBodyField && (
                <div className="body">
                  <FormSchemaInput
                    as="textarea"
                    label={
                      isMultiForm
                        ? `${FORM_FIELDS.PRODUCT_BODY} #${i + 1}`
                        : FORM_FIELDS.PRODUCT_BODY
                    }
                    key={camelize(FORM_FIELDS.PRODUCT_BODY)}
                    id={camelize(FORM_FIELDS.PRODUCT_BODY)}
                    onChange={(e) => handleUpdateField(e, data.id)}
                    value={data.fields[camelize(FORM_FIELDS.PRODUCT_BODY)]}
                  />
                </div>
              )}
              <FloatingActionsWrapper>
                <Tooltip content="Clear Block" placement="right">
                  <button
                    type="button"
                    onClick={() => handleClearBlock(data.id)}
                    aria-label="clear block"
                  >
                    <Icon type="minus-circle" />
                  </button>
                </Tooltip>
                <Tooltip content="Reset Block" placement="right">
                  <button
                    type="button"
                    onClick={() => handleResetBlock(data.id)}
                    aria-label="reset block"
                  >
                    <Icon type="reset-dot" />
                  </button>
                </Tooltip>
                {showDeleteBtn && (
                  <Tooltip content="Delete Block" placement="right">
                    <button
                      type="button"
                      aria-label="delete block"
                      onClick={() => handleDeleteFormBlock(data.id)}
                    >
                      <Icon type="trash" />
                    </button>
                  </Tooltip>
                )}
              </FloatingActionsWrapper>
            </FieldBlockWrapper>
            {i !== formData.fieldData.length - 1 && (
              <Divider styles={{ marginBottom: 16 }} />
            )}
          </div>
        ))}
        {showAddBtn && (
          <button
            type="button"
            onClick={handleAddFormBlock}
            className="add-btn"
            aria-label="add block"
          >
            <Icon type="add-circle" />
          </button>
        )}
      </form>
    </EditorFormWrapper>
  );
};

export default EditorForm;
